import type { LevelDefinition } from '../world/LevelDefinition';

const surfaces = {
  floor: { enabled: true, magnetic: true },
  ceiling: { enabled: true, magnetic: true },
  left: { enabled: true, magnetic: true },
  right: { enabled: true, magnetic: true },
  front: { enabled: true, magnetic: true },
  back: { enabled: true, magnetic: true },
};

const ceilingLights = (zs: number[]) =>
  zs.map((z) => ({
    position: [0, 4.9, z] as [number, number, number],
    size: [3, 0.08, 1.2] as [number, number, number],
  }));

/**
 * Firing Range (standard): one long hall with lanes marked by pillars and
 * consoles. It exists to read the drones' burst at range — the first shots of a
 * burst spray wide and the last ones settle, so standing in the open down a
 * clear lane is a bad idea, and so is reading the pattern and leaning into it.
 */
export const FIRING_RANGE: LevelDefinition = {
  id: 'firing-range',
  name: 'Firing Range',
  difficulty: 'standard',
  size: [14, 5, 44],
  surfaces,
  spawn: { surface: 'floor', position: [0, 0, 18], facing: Math.PI },
  targets: [
    { type: 'drone', surface: 'floor', position: [0, 0, 8] },
    { type: 'drone', surface: 'floor', position: [-4, 0, 0] },
    { type: 'drone', surface: 'floor', position: [4, 0, -8] },
    { type: 'drone', surface: 'ceiling', position: [0, 5, -2] },
    { type: 'drone', surface: 'left', position: [-7, 2.4, -14] },
    { type: 'drone', surface: 'right', position: [7, 2.4, -12] },
    { type: 'drone', surface: 'front', position: [0, 2.4, -22], magnetic: true },
  ],
  obstacles: [
    // Lane markers down the hall: full-height pillars with gaps between them.
    { type: 'box', kind: 'pillar', position: [-3.5, 2.5, 12], size: [1.2, 5, 1.2] },
    { type: 'box', kind: 'pillar', position: [3.5, 2.5, 6], size: [1.2, 5, 1.2] },
    { type: 'box', kind: 'pillar', position: [-3.5, 2.5, -2], size: [1.2, 5, 1.2] },
    { type: 'box', kind: 'pillar', position: [3.5, 2.5, -10], size: [1.2, 5, 1.2] },
    { type: 'box', kind: 'pillar', position: [-3.5, 2.5, -16], size: [1.2, 5, 1.2] },
    // Low cover to duck behind while a burst goes by.
    { type: 'box', kind: 'console', position: [0, 0.55, 10], size: [3, 1.1, 0.9] },
    { type: 'box', kind: 'console', position: [-2, 0.55, 2], size: [2.4, 1.1, 0.9] },
    { type: 'box', kind: 'console', position: [2, 0.55, -6], size: [2.4, 1.1, 0.9] },
    { type: 'box', kind: 'crate', position: [0, 0.7, -18], size: [1.4, 1.4, 1.4] },
  ],
  style: {
    wallColor: 0x2a3038,
    floorColor: 0x1d232c,
    ceilingColor: 0xc9d6e2,
    pathColor: 0xffb347,
    path: [
      { from: [0, 20], to: [0, -20], width: 3 },
      { from: [-6, 10], to: [6, 10], width: 1.4 },
      { from: [-6, -8], to: [6, -8], width: 1.4 },
    ],
    ceilingLights: ceilingLights([16, 8, 0, -8, -16]),
  },
  seed: 9001,
};
